import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  Wrench,
  CheckCircle,
  XCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import maintenanceProviderMasterService from '../../services/maintenanceProviderMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const MaintenanceProviderMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProviders: 0,
    activeProviders: 0,
    inactiveProviders: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        maintenanceProviderMasterService.getAllProviders({ limit: 1000 }),
        maintenanceProviderMasterService.getProviderStats()
      ])

      if (listResponse.success) {
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No maintenance provider records found. Create your first provider!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load maintenance providers')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading maintenance provider records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view maintenance providers')
      } else if (!error._errorHandled) {
        toast.error('Failed to load maintenance provider records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'providerCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'providerName',
      header: 'Provider Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'specialty',
      header: 'Specialty',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'contactName',
      header: 'Contact',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/maintenance-provider-master/form')
  }

  const handleEdit = (providerData) => {
    navigate('/assets/maintenance-provider-master/edit', { state: { providerData } })
  }

  const handleView = (providerData) => {
    navigate('/assets/maintenance-provider-master/view', { state: { providerData } })
  }

  const handleDelete = (providerData) => {
    setDeleteItem(providerData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting maintenance provider...')
      const response = await maintenanceProviderMasterService.deleteProvider(deleteItem._id)
      if (response.success) {
        toast.success('Maintenance provider deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete maintenance provider')
      }
    } catch (error) {
      console.error('Error deleting maintenance provider:', error)
      if (error.response?.status === 404) {
        toast.error('Maintenance provider not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this maintenance provider')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete maintenance provider. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Maintenance Providers', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Maintenance Providers</h1>
          <p>Maintenance schedules and service providers</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Provider
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalProviders || 0}</div>
          <div className="metric-title">Total Providers</div>
          <div className="metric-subtitle">All providers</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeProviders || 0}</div>
          <div className="metric-title">Active Providers</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveProviders || 0}</div>
          <div className="metric-title">Inactive Providers</div>
          <div className="metric-subtitle">Not in use</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search providers..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Maintenance Provider"
        message={`Are you sure you want to delete "${deleteItem?.providerName || deleteItem?.providerCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default MaintenanceProviderMaster
